<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card>
          <v-toolbar>
            <v-toolbar-title>
              {{ $t('Two-factor authentication') }}
            </v-toolbar-title>
          </v-toolbar>
          <template v-if="user.two_factor_auth_enabled && user.two_factor_auth_passed">
            <template v-if="displayDisablingForm">
              <v-card-text>
                <p>
                  {{ $t('Please fill in the below form to disable two-factor authentication.') }}
                </p>
                <v-form v-model="disablingFormIsValid" @submit.prevent="disable">
                  <v-text-field
                    v-if="user.profiles.length == 0"
                    v-model="disablingForm.password"
                    :label="$t('Current password')"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    name="password"
                    :rules="[validationRequired]"
                    :error="disablingForm.errors.has('password')"
                    :error-messages="disablingForm.errors.get('password')"
                    outlined
                    counter
                    @click:append="showPassword = !showPassword"
                    @keydown="clearFormErrors($event, 'password', disablingForm)"
                  />

                  <v-text-field
                    v-model="disablingForm.one_time_password"
                    :label="$t('One-time password')"
                    type="text"
                    name="one_time_password"
                    :rules="[validationRequired, v => validationMinLength(v,6)]"
                    :error="disablingForm.errors.has('one_time_password')"
                    :error-messages="disablingForm.errors.get('one_time_password')"
                    outlined
                    @keydown="clearFormErrors($event, 'one_time_password', disablingForm)"
                  />

                  <v-btn type="submit" color="primary" :disabled="!disablingFormIsValid || disablingForm.busy" :loading="disablingForm.busy">
                    {{ $t('Confirm') }}
                  </v-btn>
                </v-form>
              </v-card-text>
            </template>
            <template v-else>
              <v-card-text>
                <p>
                  {{ $t('Two-factor authentication is currently enabled.') }}
                </p>
                <v-btn color="primary" @click="displayDisablingForm = !displayDisablingForm">{{ $t('Disable') }}</v-btn>
              </v-card-text>
            </template>
          </template>
          <template v-else>
            <template v-if="qr">
              <v-card-text>
                <p>{{ $t('Please do the following to enable two-factor authentication.') }}</p>
                <ul>
                  <li>{{ $t('Open Google Authenticator app on your mobile phone.') }}</li>
                  <li>{{ $t('Scan the below QR code with the authenticator app.') }}</li>
                  <li>{{ $t('After that input the one-time password that you see on the screen to complete the process.') }}</li>
                </ul>

                <div class="my-5 text-center" v-html="qr" />

                <v-form v-model="confirmationFormIsValid" @submit.prevent="confirm">
                  <v-text-field
                    v-model="confirmationForm.one_time_password"
                    :label="$t('One-time password')"
                    type="text"
                    name="one_time_password"
                    :rules="[validationRequired, v => validationMinLength(v,6)]"
                    :error="confirmationForm.errors.has('one_time_password')"
                    :error-messages="confirmationForm.errors.get('one_time_password')"
                    outlined
                    @keydown="clearFormErrors($event, 'one_time_password', confirmationForm)"
                  />

                  <v-btn type="submit" color="primary" :disabled="!confirmationFormIsValid || confirmationForm.busy" :loading="confirmationForm.busy">
                    {{ $t('Confirm') }}
                  </v-btn>
                </v-form>
              </v-card-text>
            </template>
            <template v-else>
              <v-card-text>
                <p>
                {{ $t('2-step verification provides stronger security for your account by requiring a second step of verification when you sign in.') }}
                {{ $t('In addition to your password, you will also need a code generated by the Google Authenticator app on your phone.') }}
                {{ $t('It is strongly recommended to enable two-factor authentication to protect your account.') }}
                </p>
                <v-btn color="primary" @click="enable" :disabled="enablingForm.busy" :loading="enablingForm.busy">{{ $t('Enable') }}</v-btn>
              </v-card-text>
            </template>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Form from 'vform'
import FormMixin from '~/mixins/Form'
import { mapState } from 'vuex'

export default {
  mixins: [FormMixin],

  metaInfo () {
    return { title: this.$t('Two-factor authentication') }
  },

  data: () => ({
    showPassword: false,
    disablingFormIsValid: null,
    confirmationFormIsValid: null,
    enablingForm: new Form(),
    disablingForm: new Form(),
    confirmationForm: new Form({
      secret: '',
      one_time_password: ''
    }),
    qr: null,
    displayDisablingForm: false
  }),

  computed: {
    ...mapState('auth', [
      'user'
    ])
  },

  methods: {
    async enable () {
      const { data } = await this.enablingForm.post('/api/user/security/2fa/enable')

      this.qr = data.qr
      this.confirmationForm.secret = data.secret
    },
    async confirm () {
      const { data } = await this.confirmationForm.post('/api/user/security/2fa/confirm')

      // Update the user
      this.$store.dispatch('auth/updateUser', data)

      this.qr = null

      this.$store.dispatch('message/success', { text: this.$t('Two-factor authentication successfully enabled.') })
    },
    async disable () {
      const { data } = await this.disablingForm.post('/api/user/security/2fa/disable')

      // Fetch the user
      this.$store.dispatch('auth/updateUser', data)

      this.$store.dispatch('message/success', { text: this.$t('Two-factor authentication successfully disabled.') })
    }
  }
}
</script>
